/**
 * F0b — WBS / CLP / construction-event service module.
 *
 * Single typed layer for the 9 reworked WBS/CLP/progress screens so they
 * call a single indirection point rather than owning inline endpoint strings.
 *
 * All functions return the bare body (interceptor already strips .data).
 * camelCase params on the wire (Pydantic CamelModel convention).
 *
 * Contract coverage:
 *   §A  getWbsStages
 *   §B  getConstructionEvents / createConstructionEvent / updateConstructionEvent
 *   §C  getPhaseStageWeights / putPhaseStageWeights
 *   §D  getClpLines / createClpLine / deleteClpLine / putClpOverrides
 *   §E  getSlabPaymentPlan
 */

import axiosInstance from "../api/axios";

// ---------------------------------------------------------------------------
// §A — Stage catalog
// ---------------------------------------------------------------------------

/**
 * GET /api/wbs/stages
 * @param {object} params  Optional: { isActive, isConstruction }
 * @returns {Promise<WbsStageData[]>}
 */
export const getWbsStages = (params = {}) =>
  axiosInstance.get("/wbs/stages", { params });

// ---------------------------------------------------------------------------
// §B — Construction events (replaces building_status POST/PUT/GET)
// ---------------------------------------------------------------------------

/**
 * GET /api/projects/buildings/status
 * @param {object} params  { buildingId?, wbsStageId?, floor?, projectId?, projectPhaseId? }
 * @returns {Promise<ResponseConstructionEventData[]>}
 */
export const getConstructionEvents = (params = {}) =>
  axiosInstance.get("/projects/buildings/status", { params });

/**
 * POST /api/projects/buildings/status
 * Body: { buildingId, wbsStageId, floor, plannedDate, achievedDate }
 * @returns {Promise<ResponseConstructionEventData>}
 */
export const createConstructionEvent = (body) =>
  axiosInstance.post("/projects/buildings/status", body);

/**
 * PUT /api/projects/buildings/status?constructionEventId={id}
 * Body: { buildingId, wbsStageId, floor, plannedDate, achievedDate }
 * NOTE: the backend takes constructionEventId as a QUERY param, not in the body.
 * @returns {Promise<ResponseConstructionEventData>}
 */
export const updateConstructionEvent = (body) => {
  const { constructionEventId, ...rest } = body;
  return axiosInstance.put(
    `/projects/buildings/status?constructionEventId=${constructionEventId}`,
    rest
  );
};

/**
 * POST /api/projects/buildings/status/bulk-plan
 * Bulk-define proposed target dates for one (building, stage).
 * FLOOR-scoped stage:        { buildingId, wbsStageId, floors: [{ floor, plannedDate }] }
 * BUILDING/PROJECT-scoped:   { buildingId, wbsStageId, plannedDate }
 * Upserts plannedDate only — never touches achievedDate/percent on existing rows.
 * @returns {Promise<ResponseConstructionEventBulkPlanData>}
 */
export const bulkPlanConstructionEvents = (body) =>
  axiosInstance.post("/projects/buildings/status/bulk-plan", body);

/**
 * POST /api/projects/buildings/status/{constructionEventId}/media  (multipart)
 * Attaches a progress photo to an existing event (stages with hasMedia only).
 * @returns {Promise<ResponseConstructionEventData>}
 */
export const uploadConstructionEventMedia = (constructionEventId, file) => {
  const fd = new FormData();
  fd.append("progress_image", file);
  return axiosInstance.post(
    `/projects/buildings/status/${constructionEventId}/media`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

// ---------------------------------------------------------------------------
// §C — Per-phase stage weights (replaces wbsPercent JSON blob)
// ---------------------------------------------------------------------------

/**
 * GET /api/projects/phase-stage-weights?projectPhaseId={id}
 * @param {object} params  { projectPhaseId }
 * @returns {Promise<ProjectPhaseStageData[]>}
 */
export const getPhaseStageWeights = (params) =>
  axiosInstance.get("/projects/phase-stage-weights", { params });

/**
 * PUT /api/projects/phase-stage-weights
 * Body: { projectPhaseId, stages: [{ wbsStageId, weight, isActive }] }
 * @returns {Promise<ProjectPhaseStageData[]>}
 */
export const putPhaseStageWeights = (body) =>
  axiosInstance.put("/projects/phase-stage-weights", body);

// ---------------------------------------------------------------------------
// §D — CLP lines (replaces project_status)
// ---------------------------------------------------------------------------

/**
 * GET /api/projects/clp-lines?constructionLinkedPlanId={id}
 * @param {object} params  { constructionLinkedPlanId }
 * @returns {Promise<ResponseClpLineData[]>}
 */
export const getClpLines = (params) =>
  axiosInstance.get("/projects/clp-lines", { params });

/**
 * POST /api/projects/clp-lines
 * Body: { constructionLinkedPlanId, wbsStageId, order, particulars, percentConsideration, floorTarget }
 * @returns {Promise<ResponseClpLineData>}
 */
export const createClpLine = (body) =>
  axiosInstance.post("/projects/clp-lines", body);

/**
 * DELETE /api/projects/clp-lines/{clpLineId} → 204
 * @param {number} clpLineId
 */
export const deleteClpLine = (clpLineId) =>
  axiosInstance.delete(`/projects/clp-lines/${clpLineId}`);

/**
 * PUT /api/sales/clp-overrides
 * Body: { customerSalesId, wbsStageId, floorTarget, percentConsideration }
 * @returns {Promise<ResponseClpOverrideData[]>}
 */
export const putClpOverrides = (body) =>
  axiosInstance.put("/sales/clp-overrides", body);

// ---------------------------------------------------------------------------
// §E — Buyer slab / payment-plan read
// ---------------------------------------------------------------------------

/**
 * GET /api/property/slab/payment-plan?propertyId={id}&totalAmount={amt}
 * @param {object} params  { propertyId, totalAmount }
 * @returns {Promise<PropertySlabPaymentPlan[]>}
 */
export const getSlabPaymentPlan = (params) =>
  axiosInstance.get("/property/slab/payment-plan", { params });
